import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import {
    FiActivity,
    FiArrowLeft,
    FiCalendar,
    FiCheck,
    FiChevronDown,
    FiCreditCard,
    FiDollarSign,
    FiFilter,
    FiPlus,
    FiSearch,
    FiTrendingDown,
    FiTrendingUp,
    FiX,
} from "react-icons/fi";
import tr from "date-fns/locale/tr";
import { firestore } from "../../utils/firebase";
import { getReportDateKey } from "../../services/reporting";
import "./Muhasebe.css";

const DEFAULT_CATEGORIES = ["Satış", "Personel", "Kira", "Tedarik", "Fatura", "Diğer"];

const formatCurrency = (value) =>
    new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 2,
    }).format(Number(value) || 0);

const formatDate = (date) =>
    date.toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });

const parseAmount = (amount) => Number(String(amount).replace(",", "."));

export default function Muhasebe() {
    const history = useHistory();
    const cafe = localStorage.getItem("name");
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("Tümü");
    const [entryType, setEntryType] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ category: "", name: "", amount: "" });
    const [refresh, setRefresh] = useState(0);

    const dateKey = getReportDateKey(selectedDate);

    useEffect(() => {
        let active = true;

        const loadTransactions = async () => {
            try {
                const [expenseSnapshot, salesSnapshot] = await Promise.all([
                    firestore.collection(`Restaurants/${cafe}/expense/${dateKey}/data`).get(),
                    firestore.collection(`Restaurants/${cafe}/sales`).doc(dateKey).get(),
                ]);

                if (!active) return;

                const expenses = expenseSnapshot.docs.map((doc) => ({
                    ...doc.data(),
                    id: doc.id,
                    source: "Manuel kayıt",
                }));
                const sales = salesSnapshot.exists ? salesSnapshot.data() : {};
                const automaticIncome = [
                    {
                        id: `sales-total-${dateKey}`,
                        category: "Satış",
                        name: "Satış tahsilatları",
                        amount: (Number(sales.nakit) || 0) +
                            (Number(sales.kredi) || 0) +
                            (Number(sales.mealCard) || 0),
                        source: "Otomatik",
                    },
                ].filter((item) => item.amount > 0);

                const allTransactions = [...automaticIncome, ...expenses];
                setTransactions(allTransactions);
                setCategories(Array.from(new Set([
                    ...DEFAULT_CATEGORIES,
                    ...expenses.map((item) => item.category).filter(Boolean),
                ])));
            } catch (error) {
                console.error("Muhasebe verileri yüklenemedi:", error);
                if (active) toast.error("Muhasebe verileri yüklenemedi.");
            }
        };

        loadTransactions();
        return () => {
            active = false;
        };
    }, [cafe, dateKey, refresh]);

    const visibleTransactions = useMemo(() => {
        const query = search.trim().toLocaleLowerCase("tr-TR");
        return transactions.filter((item) => {
            const matchesFilter = filter === "Tümü"
                || (filter === "Gelir" && !item.outcome)
                || (filter === "Gider" && item.outcome)
                || item.category === filter;
            const matchesSearch = !query
                || [item.name, item.category, item.source]
                    .filter(Boolean)
                    .some((value) => value.toLocaleLowerCase("tr-TR").includes(query));
            return matchesFilter && matchesSearch;
        });
    }, [filter, search, transactions]);

    const income = useMemo(
        () => transactions.filter((item) => !item.outcome).reduce((total, item) => total + (parseAmount(item.amount) || 0), 0),
        [transactions]
    );
    const expense = useMemo(
        () => transactions.filter((item) => item.outcome).reduce((total, item) => total + (parseAmount(item.amount) || 0), 0),
        [transactions]
    );
    const balance = income - expense;
    const categoryFilterValue = filter === "Gelir" || filter === "Gider" ? "Tümü" : filter;
    const categoryTotals = useMemo(() => {
        const totals = {};
        transactions.filter((item) => item.outcome).forEach((item) => {
            totals[item.category || "Diğer"] = (totals[item.category || "Diğer"] || 0) + (parseAmount(item.amount) || 0);
        });
        return Object.entries(totals).sort((a, b) => b[1] - a[1]).slice(0, 4);
    }, [transactions]);

    const openEntryModal = (type) => {
        setEntryType(type);
        setForm({ category: type === "income" ? "Satış" : "", name: "", amount: "" });
    };

    const closeEntryModal = () => {
        if (!saving) setEntryType(null);
    };

    const saveNewEntry = async (event) => {
        event.preventDefault();
        const amount = parseAmount(form.amount);
        if (!form.name.trim() || !form.category.trim() || !Number.isFinite(amount) || amount <= 0) {
            toast.warn("Kategori, açıklama ve geçerli bir miktar girin.");
            return;
        }

        setSaving(true);
        const id = uuid();
        try {
            await firestore
                .collection(`Restaurants/${cafe}/expense/${dateKey}/data`)
                .doc(id)
                .set({
                    id,
                    name: form.name.trim(),
                    category: form.category.trim(),
                    amount,
                    time: new Date().toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }),
                    outcome: entryType === "expense",
                });
            toast.success(entryType === "income" ? "Gelir kaydedildi." : "Gider kaydedildi.");
            setEntryType(null);
            setRefresh((value) => value + 1);
        } catch (error) {
            console.error("Muhasebe kaydı eklenemedi:", error);
            toast.error("Kayıt eklenemedi. Lütfen tekrar deneyin.");
        } finally {
            setSaving(false);
        }
    };

    const changeDate = (date) => {
        if (!date) return;
        setSelectedDate(date);
        setShowDatePicker(false);
        setFilter("Tümü");
        setSearch("");
    };

    return (
        <main className="accounting-page">
            <div className="accounting-shell">
                <header className="accounting-header">
                    <div className="accounting-heading">
                        <button className="back-button" type="button" onClick={() => history.push("/admin")} aria-label="Panele dön"><FiArrowLeft /></button>
                        <div><span className="eyebrow">FİNANS MERKEZİ</span><h1>Gelir &amp; Gider</h1><p>Günün finansal hareketlerini tek ekrandan yönetin.</p></div>
                    </div>
                    <div className="header-actions">
                        <div className="date-picker-wrap">
                            <button className="date-button" type="button" onClick={() => setShowDatePicker((value) => !value)}><FiCalendar /><span>{formatDate(selectedDate)}</span><FiChevronDown className={showDatePicker ? "rotated" : ""} /></button>
                            {showDatePicker && <div className="date-picker-popover"><DayPicker mode="single" locale={tr} selected={selectedDate} onSelect={changeDate} /></div>}
                        </div>
                        <button className="primary-action" type="button" onClick={() => openEntryModal("income")}><FiPlus /> Gelir ekle</button>
                        <button className="secondary-action" type="button" onClick={() => openEntryModal("expense")}><FiPlus /> Gider ekle</button>
                    </div>
                </header>

                <section className="summary-grid" aria-label="Finans özeti">
                    <article className={`summary-card balance-card ${balance < 0 ? "negative" : ""}`}><div className="card-icon balance-icon"><FiDollarSign /></div><div><span className="card-label">Günlük net bakiye</span><strong>{formatCurrency(balance)}</strong><small><FiActivity /> {balance >= 0 ? "Bugün pozitif ilerliyor" : "Giderler geliri aşıyor"}</small></div></article>
                    <article className="summary-card"><div className="card-icon income-icon"><FiTrendingUp /></div><div><span className="card-label">Toplam gelir</span><strong>{formatCurrency(income)}</strong><small className="positive-text">Gelir kalemleri</small></div></article>
                    <article className="summary-card"><div className="card-icon expense-icon"><FiTrendingDown /></div><div><span className="card-label">Toplam gider</span><strong>{formatCurrency(expense)}</strong><small className="negative-text">Gider kalemleri</small></div></article>
                    <article className="summary-card"><div className="card-icon count-icon"><FiCreditCard /></div><div><span className="card-label">İşlem sayısı</span><strong>{transactions.length}</strong><small>Seçili gün</small></div></article>
                </section>

                <section className="overview-grid">
                    <article className="panel distribution-panel">
                        <div className="panel-heading"><div><span className="eyebrow">GÜNÜN ÖZETİ</span><h2>Finansal dağılım</h2></div><span className={`balance-pill ${balance < 0 ? "is-negative" : ""}`}>{balance >= 0 ? "Pozitif" : "Negatif"}</span></div>
                        <div className="distribution-total"><span>Toplam hareket</span><strong>{formatCurrency(income + expense)}</strong></div>
                        <div className="distribution-bar" aria-label="Gelir ve gider oranı"><span className="income-bar" style={{ width: `${income + expense ? (income / (income + expense)) * 100 : 0}%` }} /><span className="expense-bar" style={{ width: `${income + expense ? (expense / (income + expense)) * 100 : 0}%` }} /></div>
                        <div className="legend-row"><span><i className="legend-dot income-dot" /> Gelir <b>{formatCurrency(income)}</b></span><span><i className="legend-dot expense-dot" /> Gider <b>{formatCurrency(expense)}</b></span></div>
                        <div className="category-breakdown"><div className="breakdown-title"><span>En yüksek gider kategorileri</span><FiFilter /></div>{categoryTotals.length === 0 ? <p className="empty-breakdown">Henüz gider kaydı yok.</p> : categoryTotals.map(([category, amount]) => <div className="category-row" key={category}><span>{category}</span><div className="category-progress"><i style={{ width: `${expense ? (amount / expense) * 100 : 0}%` }} /></div><strong>{formatCurrency(amount)}</strong></div>)}</div>
                    </article>
                    <article className="panel quick-panel"><span className="eyebrow">HIZLI İŞLEMLER</span><h2>Bugün ne eklemek istersiniz?</h2><p>Manuel finans hareketlerini seçili güne kaydedin.</p><div className="quick-actions"><button type="button" className="quick-income" onClick={() => openEntryModal("income")}><span><FiTrendingUp /></span><b>Yeni gelir</b><small>Kasaya giriş ekle</small><FiPlus /></button><button type="button" className="quick-expense" onClick={() => openEntryModal("expense")}><span><FiTrendingDown /></span><b>Yeni gider</b><small>Harcamayı kaydet</small><FiPlus /></button></div></article>
                </section>

                <section className="panel transactions-panel">
                    <div className="transactions-heading"><div><span className="eyebrow">HAREKETLER</span><h2>Son işlemler</h2></div><span className="transaction-count">{visibleTransactions.length} kayıt gösteriliyor</span></div>
                    <div className="transactions-toolbar"><div className="search-field"><FiSearch /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="İşlem veya kategori ara..." aria-label="İşlem ara" /></div><div className="filter-tabs" role="tablist" aria-label="İşlem filtreleri">{["Tümü", "Gelir", "Gider"].map((item) => <button key={item} type="button" className={filter === item ? "active" : ""} onClick={() => setFilter(item)}>{item}</button>)}</div><select className="category-select" value={categoryFilterValue} onChange={(event) => setFilter(event.target.value)} aria-label="Kategori filtrele"><option value="Tümü">Tüm kategoriler</option>{categories.map((category) => <option key={category} value={category}>{category}</option>)}</select></div>
                    <div className="table-wrap"><table className="transactions-table"><thead><tr><th>İŞLEM</th><th>KATEGORİ</th><th>KAYNAK</th><th>TUTAR</th><th>DURUM</th></tr></thead><tbody>{visibleTransactions.length === 0 ? <tr><td colSpan="5" className="empty-state"><div><FiSearch /><strong>Bu güne ait kayıt bulunamadı</strong><span>Yeni bir gelir veya gider ekleyerek başlayın.</span></div></td></tr> : visibleTransactions.map((item) => <tr key={item.id}><td><div className="transaction-name"><span className={`transaction-icon ${item.outcome ? "outcome" : "income"}`}>{item.outcome ? <FiTrendingDown /> : <FiTrendingUp />}</span><div><strong>{item.name}</strong><small>{item.time || "Otomatik satış"}</small></div></div></td><td><span className="category-tag">{item.category || "Diğer"}</span></td><td><span className={`source-tag ${item.source === "Otomatik" ? "automatic" : ""}`}>{item.source || "Manuel kayıt"}</span></td><td><strong className={item.outcome ? "amount-expense" : "amount-income"}>{item.outcome ? "-" : "+"}{formatCurrency(item.amount)}</strong></td><td><span className={`status-tag ${item.outcome ? "expense-status" : "income-status"}`}><FiCheck /> {item.outcome ? "Gider" : "Gelir"}</span></td></tr>)}</tbody></table></div>
                </section>
            </div>

            {entryType && <div className="modal-backdrop" onMouseDown={closeEntryModal}><div className="entry-modal" role="dialog" aria-modal="true" aria-labelledby="entry-modal-title" onMouseDown={(event) => event.stopPropagation()}><div className="modal-heading"><div className={`modal-icon ${entryType}`}><FiPlus /></div><div><span className="eyebrow">YENİ KAYIT</span><h2 id="entry-modal-title">{entryType === "income" ? "Gelir ekle" : "Gider ekle"}</h2></div><button type="button" className="close-modal" onClick={closeEntryModal} aria-label="Pencereyi kapat"><FiX /></button></div><p className="modal-description">Bu kayıt <strong>{formatDate(selectedDate)}</strong> tarihine eklenecek.</p><form onSubmit={saveNewEntry}><label>Kategori<input list="accounting-categories" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} placeholder="Örn. Tedarik" autoFocus /><datalist id="accounting-categories">{categories.map((category) => <option key={category} value={category} />)}</datalist></label><label>Açıklama<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="İşlem açıklaması" /></label><label>Tutar<input type="text" inputMode="decimal" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} placeholder="0,00 ₺" /></label><div className="modal-actions"><button type="button" className="cancel-button" onClick={closeEntryModal}>Vazgeç</button><button type="submit" className={`save-button ${entryType}`} disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}<FiCheck /></button></div></form></div></div>}
        </main>
    );
}
